import React, { Fragment, useCallback, useEffect, useMemo, useState } from "react";
import DataTable from "react-data-table-component";
import {
  Alert,
  Badge,
  Button,
  Col,
  Form,
  FormGroup,
  Input,
  Label,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  Row,
  Spinner,
  Table,
} from "reactstrap";
import { Breadcrumbs } from "../../../AbstractElements";
import { fetchBranches } from "../../../api/users";
import {
  createJournalEntry,
  fetchChartOfAccounts,
  fetchFinancialPeriods,
  fetchJournalEntries,
  postJournalEntry,
} from "../../../api/accounting";
import { formatDate, formatTZS } from "../../../utils/format";
import { useConfirmDialog } from "../Common/ConfirmDialogContext";
import { DEFAULT_TABLE_CUSTOM_STYLES } from "../Common/credoraTableUtils";
import { formatApiError, runCrud, runLoad } from "../Common/credoraToast";
import { ACCOUNTING_SECTION } from "./accountingPlaceholders";
import AccountingIconActions from "./AccountingIconActions";
import { JOURNAL_STATUS_META, todayIsoDate, useLatestRef } from "./accountingUtils";

const emptyLine = () => ({ account_id: "", debit: "", credit: "", description: "" });

const JournalEntriesPage = () => {
  const { confirm } = useConfirmDialog();
  const [branches, setBranches] = useState([]);
  const [branchId, setBranchId] = useState("");
  const [periods, setPeriods] = useState([]);
  const [accounts, setAccounts] = useState([]);
  const [journals, setJournals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [modalOpen, setModalOpen] = useState(false);
  const [detail, setDetail] = useState(null);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    period_id: "",
    entry_date: todayIsoDate(),
    description: "",
    lines: [emptyLine(), emptyLine()],
  });

  useEffect(() => {
    fetchBranches(true).then((rows) => {
      setBranches(rows || []);
      if (rows?.length) setBranchId(String(rows[0].id));
    });
  }, []);

  const loadMeta = useCallback(async () => {
    if (!branchId) return;
    const [p, a] = await Promise.all([
      fetchFinancialPeriods(branchId),
      fetchChartOfAccounts({ branch_id: branchId, active_only: true }),
    ]);
    setPeriods((p || []).filter((x) => x.status === "open"));
    setAccounts(a || []);
    if (p?.length && !form.period_id) {
      const open = p.find((x) => x.status === "open");
      if (open) setForm((prev) => ({ ...prev, period_id: open.id }));
    }
  }, [branchId, form.period_id]);

  const load = useCallback(() => {
    if (!branchId) return Promise.resolve();
    setLoading(true);
    setError("");
    const params = { branch_id: branchId };
    if (statusFilter) params.status = statusFilter;
    return runLoad(
      () =>
        Promise.all([fetchJournalEntries(params).then(setJournals), loadMeta()]),
      { errorMessage: "Failed to load journal entries." }
    )
      .catch((err) => setError(formatApiError(err, "Failed to load journal entries.")))
      .finally(() => setLoading(false));
  }, [branchId, statusFilter, loadMeta]);

  useEffect(() => {
    load();
  }, [load]);

  const loadRef = useLatestRef(load);

  const postEntry = useCallback(
    (row) => {
      confirm({
        title: `Post ${row.entry_number}?`,
        message: "Posted entries update the general ledger and cannot be edited.",
        confirmLabel: "Post entry",
        tone: "primary",
        successMessage: "Journal entry posted.",
        onConfirm: async () => {
          await postJournalEntry(row.id);
          await loadRef.current();
        },
      });
    },
    [confirm, loadRef]
  );

  const columns = useMemo(
    () => [
      { name: "Number", selector: (r) => r.entry_number, sortable: true, width: "120px" },
      {
        name: "Date",
        selector: (r) => r.entry_date,
        cell: (r) => formatDate(r.entry_date),
        width: "110px",
      },
      { name: "Description", selector: (r) => r.description, grow: 2 },
      {
        name: "Debit",
        selector: (r) => Number(r.total_debit),
        cell: (r) => formatTZS(r.total_debit),
        right: true,
        width: "130px",
      },
      {
        name: "Credit",
        selector: (r) => Number(r.total_credit),
        cell: (r) => formatTZS(r.total_credit),
        right: true,
        width: "130px",
      },
      {
        name: "Status",
        cell: (r) => {
          const meta = JOURNAL_STATUS_META[r.status] || { label: r.status, color: "secondary" };
          return <Badge color={meta.color}>{meta.label}</Badge>;
        },
        width: "100px",
      },
      {
        name: "Actions",
        cell: (r) => (
          <AccountingIconActions
            actions={[
              {
                icon: "eye",
                label: "View entry",
                onClick: () => setDetail(r),
              },
              r.status === "draft" && {
                icon: "check",
                label: "Post entry",
                onClick: () => postEntry(r),
                variant: "primary",
              },
            ].filter(Boolean)}
          />
        ),
        width: "88px",
        ignoreRowClick: true,
      },
    ],
    [postEntry]
  );

  const updateLine = (idx, field, value) => {
    setForm((prev) => {
      const lines = [...prev.lines];
      lines[idx] = { ...lines[idx], [field]: value };
      return { ...prev, lines };
    });
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const lines = form.lines
        .filter((l) => l.account_id && (Number(l.debit) > 0 || Number(l.credit) > 0))
        .map((l) => ({
          account_id: l.account_id,
          description: l.description || null,
          debit: Number(l.debit) || 0,
          credit: Number(l.credit) || 0,
        }));
      await runCrud(
        () =>
          createJournalEntry({
            branch_id: branchId,
            period_id: form.period_id,
            entry_date: form.entry_date,
            description: form.description,
            lines,
          }),
        { successMessage: "Journal entry created." }
      );
      setModalOpen(false);
      setForm({
        period_id: form.period_id,
        entry_date: todayIsoDate(),
        description: "",
        lines: [emptyLine(), emptyLine()],
      });
      load();
    } finally {
      setSaving(false);
    }
  };

  return (
    <Fragment>
      <Breadcrumbs mainTitle={ACCOUNTING_SECTION} parent={ACCOUNTING_SECTION} title="Journal Entries" />
      <div className="container-fluid">
        {error && <Alert color="danger">{error}</Alert>}
        <div className="card">
          <div className="card-header d-flex flex-wrap justify-content-between align-items-center gap-2">
            <h5 className="mb-0">Journal register</h5>
            <div className="d-flex flex-wrap gap-2">
              <Input type="select" bsSize="sm" style={{ width: "160px" }} value={branchId} onChange={(e) => setBranchId(e.target.value)}>
                {branches.map((b) => (
                  <option key={b.id} value={b.id}>{b.name}</option>
                ))}
              </Input>
              <Input type="select" bsSize="sm" style={{ width: "120px" }} value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
                <option value="">All status</option>
                <option value="draft">Draft</option>
                <option value="posted">Posted</option>
              </Input>
              <Button color="primary" size="sm" onClick={() => setModalOpen(true)} disabled={!periods.length}>
                New entry
              </Button>
            </div>
          </div>
          <div className="card-body p-0">
            {loading ? (
              <div className="text-center py-5"><Spinner color="primary" /></div>
            ) : (
              <DataTable columns={columns} data={journals} customStyles={DEFAULT_TABLE_CUSTOM_STYLES} pagination />
            )}
          </div>
        </div>

        <Modal isOpen={modalOpen} toggle={() => setModalOpen(false)} size="xl">
          <Form onSubmit={handleCreate}>
            <ModalHeader toggle={() => setModalOpen(false)}>New journal entry</ModalHeader>
            <ModalBody>
              <Row className="mb-3">
                <Col md="4">
                  <FormGroup>
                    <Label>Period</Label>
                    <Input type="select" required value={form.period_id} onChange={(e) => setForm({ ...form, period_id: e.target.value })}>
                      <option value="">Select period</option>
                      {periods.map((p) => (
                        <option key={p.id} value={p.id}>{p.name}</option>
                      ))}
                    </Input>
                  </FormGroup>
                </Col>
                <Col md="4">
                  <FormGroup>
                    <Label>Entry date</Label>
                    <Input type="date" required value={form.entry_date} onChange={(e) => setForm({ ...form, entry_date: e.target.value })} />
                  </FormGroup>
                </Col>
                <Col md="12">
                  <FormGroup>
                    <Label>Description</Label>
                    <Input required value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} />
                  </FormGroup>
                </Col>
              </Row>
              <Table size="sm" bordered responsive>
                <thead>
                  <tr>
                    <th>Account</th>
                    <th>Description</th>
                    <th>Debit</th>
                    <th>Credit</th>
                    <th />
                  </tr>
                </thead>
                <tbody>
                  {form.lines.map((line, idx) => (
                    <tr key={idx}>
                      <td>
                        <Input type="select" bsSize="sm" value={line.account_id} onChange={(e) => updateLine(idx, "account_id", e.target.value)}>
                          <option value="">Select account</option>
                          {accounts.map((a) => (
                            <option key={a.id} value={a.id}>{a.code} — {a.name}</option>
                          ))}
                        </Input>
                      </td>
                      <td>
                        <Input bsSize="sm" value={line.description} onChange={(e) => updateLine(idx, "description", e.target.value)} />
                      </td>
                      <td>
                        <Input bsSize="sm" type="number" min="0" step="0.01" value={line.debit} onChange={(e) => updateLine(idx, "debit", e.target.value)} />
                      </td>
                      <td>
                        <Input bsSize="sm" type="number" min="0" step="0.01" value={line.credit} onChange={(e) => updateLine(idx, "credit", e.target.value)} />
                      </td>
                      <td>
                        {form.lines.length > 2 && (
                          <Button size="sm" color="link" className="text-danger" type="button" onClick={() => setForm((prev) => ({ ...prev, lines: prev.lines.filter((_, i) => i !== idx) }))}>
                            Remove
                          </Button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
              <Button size="sm" color="light" className="border" type="button" onClick={() => setForm((prev) => ({ ...prev, lines: [...prev.lines, emptyLine()] }))}>
                Add line
              </Button>
            </ModalBody>
            <ModalFooter>
              <Button color="secondary" type="button" onClick={() => setModalOpen(false)}>Cancel</Button>
              <Button color="primary" type="submit" disabled={saving}>{saving ? "Saving…" : "Save draft"}</Button>
            </ModalFooter>
          </Form>
        </Modal>

        <Modal isOpen={!!detail} toggle={() => setDetail(null)} size="lg">
          <ModalHeader toggle={() => setDetail(null)}>{detail?.entry_number}</ModalHeader>
          <ModalBody>
            {detail && (
              <>
                <p className="text-muted mb-2">{detail.description}</p>
                <Table size="sm" bordered responsive>
                  <thead>
                    <tr>
                      <th>Account</th>
                      <th>Description</th>
                      <th className="text-end">Debit</th>
                      <th className="text-end">Credit</th>
                    </tr>
                  </thead>
                  <tbody>
                    {detail.lines.map((line) => (
                      <tr key={line.id}>
                        <td>{line.account_code} — {line.account_name}</td>
                        <td>{line.description || "—"}</td>
                        <td className="text-end">{formatTZS(line.debit)}</td>
                        <td className="text-end">{formatTZS(line.credit)}</td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              </>
            )}
          </ModalBody>
        </Modal>
      </div>
    </Fragment>
  );
};

export default JournalEntriesPage;
